import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate } from 'react-router-dom';
import './Dashboard.scss';

interface AdminStats {
  totalUsers: number;
  totalEmployers: number;
  totalEmployees: number;
  totalJobs: number;
  activeJobs: number;
  totalApplications: number;
}

const AdminDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalEmployers: 0,
    totalEmployees: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0
  });

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an admin
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.user_type !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleManageUsers = () => {
    // Navigate to user management page when implemented
    showToast('Vartotojų valdymas bus pridėtas netrukus', 'info');
  };

  const handleManageJobs = () => {
    // Navigate to job management page when implemented
    showToast('Skelbimų valdymas bus pridėtas netrukus', 'info');
  };

  const handleViewReports = () => {
    // Navigate to reports page when implemented
    showToast('Ataskaitos bus pridėtos netrukus', 'info');
  };

  const handleManageCVs = () => {
    // Navigate to CV management page when implemented
    navigate('/admin/cvs');
  };

  const handleManageCVTemplates = () => {
    // Navigate to CV template management page
    navigate('/admin/cv-templates');
  };

  const handleManageServicePrices = () => {
    // Navigate to service prices management page
    navigate('/admin/service-prices');
  };

  const handleManageCompanies = () => {
    // Navigate to company management page
    navigate('/admin/companies');
  };

  const handleSystemSettings = () => {
    // Navigate to system settings when implemented
    showToast('Sistemos nustatymai bus pridėti netrukus', 'info');
  };

  return (
    <div className="admin-dashboard">
      <section className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="welcome-section">
              <h1>Administratoriaus skydelis</h1>
              <p className="subtitle">Valdykite sistemą ir stebėkite veiklą</p>
            </div>
            
            <div className="admin-badge">
              <span className="badge">ADMIN</span>
            </div>
          </div>
        </div>
      </section>

      {/* ADMIN STATS */}
      <section className="dashboard-stats">
        <div className="container">
          <h2>Sistemos statistika</h2>
          <div className="stats-grid admin-stats">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{stats.totalUsers}</h3>
                <p>Viso vartotojų</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>{stats.totalEmployers}</h3>
                <p>Darbdavių</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">👤</div>
              <div className="stat-content">
                <h3>{stats.totalEmployees}</h3>
                <p>Darbuotojų</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>{stats.totalJobs}</h3>
                <p>Viso skelbimų</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">🌟</div>
              <div className="stat-content">
                <h3>{stats.activeJobs}</h3>
                <p>Aktyvūs skelbimai</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">📧</div>
              <div className="stat-content">
                <h3>{stats.totalApplications}</h3>
                <p>Viso prašymų</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADMIN FEATURES */}
      <section className="admin-features">
        <div className="container">
          <h2>Administratoriaus įrankiai</h2>
          <div className="features-grid">
            <div className="feature-card admin-feature" onClick={handleManageUsers}>
              <div className="feature-icon">👥</div>
              <h3>Vartotojų valdymas</h3>
              <p>Valdykite vartotojų paskyras, teises ir statusus</p>
              <button className="feature-btn admin-btn">Valdyti vartotojus</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleManageJobs}>
              <div className="feature-icon">📋</div>
              <h3>Skelbimų moderavimas</h3>
              <p>Peržiūrėkite, tvirtinkite ar šalinkite darbo skelbimus</p>
              <button className="feature-btn admin-btn">Moderuoti skelbimus</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleViewReports}>
              <div className="feature-icon">📊</div>
              <h3>Ataskaitos ir analitika</h3>
              <p>Peržiūrėkite detalizuotas sistemos ataskaitas</p>
              <button className="feature-btn admin-btn">Žiūrėti ataskaitas</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleManageCVs}>
              <div className="feature-icon">📄</div>
              <h3>CV valdymas</h3>
              <p>Peržiūrėkite ir valdykite darbuotojų CV</p>
              <button className="feature-btn admin-btn">Valdyti CV</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleManageCVTemplates}>
              <div className="feature-icon">📝</div>
              <h3>CV šablonų valdymas</h3>
              <p>Valdykite CV formos pasirinkimo variantus</p>
              <button className="feature-btn admin-btn">Valdyti šablonus</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleManageServicePrices}>
              <div className="feature-icon">💰</div>
              <h3>Paslaugų kainos</h3>
              <p>Valdykite paslaugų kainas ir tarifus</p>
              <button className="feature-btn admin-btn">Valdyti kainas</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleManageCompanies}>
              <div className="feature-icon">🏢</div>
              <h3>Įmonių valdymas</h3>
              <p>Tvirtinkite ir valdykite įmones iš CV</p>
              <button className="feature-btn admin-btn">Valdyti įmones</button>
            </div>
            
            <div className="feature-card admin-feature" onClick={handleSystemSettings}>
              <div className="feature-icon">⚙️</div>
              <h3>Sistemos nustatymai</h3>
              <p>Konfigūruokite sistemos parametrus ir nustatymus</p>
              <button className="feature-btn admin-btn">Nustatymai</button>
            </div>
            
            <div className="feature-card admin-feature">
              <div className="feature-icon">🔒</div>
              <h3>Saugumas</h3>
              <p>Valdykite saugumo politikas ir prieigos teises</p>
              <button className="feature-btn admin-btn">Saugumo nustatymai</button>
            </div>
            
            <div className="feature-card admin-feature">
              <div className="feature-icon">📝</div>
              <h3>Sistemos žurnalas</h3>
              <p>Peržiūrėkite sistemos veiklos žurnalą</p>
              <button className="feature-btn admin-btn">Žiūrėti žurnalą</button>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT ADMIN ACTIVITY */}
      <section className="recent-activity">
        <div className="container">
          <h2>Paskutinė sistemos veikla</h2>
          <div className="activity-list admin-activity">
            <div className="activity-item">
              <div className="activity-icon">👤</div>
              <div className="activity-content">
                <p><strong>Naujas vartotojas:</strong> employer@example.com (Darbdavys)</p>
                <span className="activity-time">Prieš 30 minučių</span>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon">📋</div>
              <div className="activity-content">
                <p><strong>Skelbimas laukia moderavimo:</strong> "Senior Developer pozicija"</p>
                <span className="activity-time">Prieš 1 valandą</span>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon">⚠️</div>
              <div className="activity-content">
                <p><strong>Pranešimas apie netinkamą turinį:</strong> Skelbimas ID: 123</p>
                <span className="activity-time">Prieš 2 valandas</span>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon">🔒</div>
              <div className="activity-content">
                <p><strong>Vartotojo paskyra užblokuota:</strong> suspicious@example.com</p>
                <span className="activity-time">Prieš 3 valandas</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
